<template>
  <div class="subscription-management">
    <!-- Loading State -->
    <div v-if="loading" class="subscription-loading">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>

    <div v-else class="subscription-content">
      <!-- Plans Section -->
      <themed-card>
        <template #header>
          <h3 class="text-lg font-medium">Plans</h3>
        </template>

        <div class="subscription-plans">
          <label
            v-for="plan in plans"
            :key="plan.id"
            :class="['subscription-plan-card', { 'is-active': hasSubscription(plan) }]"
            role="radio"
            :aria-checked="hasSubscription(plan)"
            tabindex="0"
          >
            <input type="radio" class="sr-only" :checked="hasSubscription(plan)" />

            <div v-if="plan.product?.images?.length" class="subscription-plan-image">
              <img :src="plan.product.images[0]" :alt="(plan.product.name || 'Plan') + ' image'" />
            </div>

            <h4 class="subscription-plan-name">{{ plan.product?.name || plan.name || 'Plan' }}</h4>
            <p class="subscription-plan-description">{{ plan.product?.description || plan.description || '' }}</p>
            
            <div class="subscription-plan-price">
              <span class="price-amount">{{ formatCurrencyCents(plan.price, plan.currency) }}</span>
              <span class="price-interval">/{{ plan.interval }}</span>
            </div>

            <ul v-if="Array.isArray(plan.product?.marketing_features) && plan.product.marketing_features.length" class="subscription-plan-features">
              <li v-for="feature in plan.product.marketing_features" :key="getFeatureKey(feature)">
                <theme-icon icon="check" type="icons" class="feature-check" />
                {{ getFeatureName(feature) }}
              </li>
            </ul>

            <div class="subscription-plan-action">
              <themed-badge v-if="plan.has_subscription && plan.subscription_status === 'active'" variant="default">
                Current Subscription
              </themed-badge>
              <themed-badge v-else-if="plan.has_subscription && plan.subscription_status === 'canceled'" variant="secondary">
                Canceled
              </themed-badge>
              <themed-button
                v-else-if="plan.is_bundle"
                @click.stop.prevent="selectPlan(plan)"
                class="w-full"
              >
                Select Plan
              </themed-button>
              <themed-button v-else variant="secondary" disabled class="w-full">
                Not Available
              </themed-button>

              <div v-if="plan.has_subscription" class="subscription-plan-actions">
                <themed-button
                  v-if="!plan.cancel_at_period_end && showCancelSubscriptionButton"
                  variant="destructive"
                  size="sm"
                  @click.stop.prevent="confirmCancelSubscription(plan, $event)"
                  :disabled="cancellingSubscription"
                  class="w-full mt-2"
                >
                  <span v-if="cancellingSubscription === plan.id">Cancelling...</span>
                  <span v-else>Cancel Subscription</span>
                </themed-button>
                <div v-else-if="plan.cancel_at_period_end" class="subscription-cancel-notice">
                  Subscription will end on {{ formatDate(plan.period_end) }}
                </div>
              </div>
            </div>
          </label>
        </div>
      </themed-card>

      <!-- Add-ons Section -->
      <themed-card class="mt-8">
        <template #header>
          <h3 class="text-lg font-medium">Add-ons</h3>
        </template>

        <div class="subscription-addons">
          <div v-for="addon in addons" :key="addon.id" class="subscription-addon-card">
            <div class="subscription-addon-header">
              <div v-if="addon.product?.images?.length" class="subscription-addon-image">
                <img :src="addon.product.images[0]" :alt="(addon.product.name || 'Addon') + ' image'" />
              </div>
              <div class="subscription-addon-info">
                <h4 class="subscription-addon-name">{{ addon.product?.name || 'Addon' }}</h4>
                <p class="subscription-addon-description">{{ addon.product?.description }}</p>
                <themed-badge v-if="addon.has_subscription && addon.subscription_status === 'active'" variant="default" class="mt-1">
                  Active
                </themed-badge>
              </div>
              <p class="subscription-addon-price">
                {{ formatCurrencyCents(addon.price, addon.currency) }}
                <span class="price-interval">/{{ addon.interval }}</span>
              </p>
            </div>

            <div class="subscription-addon-quantity">
              <themed-input
                type="number"
                :min="getBlockSize(addon)"
                :step="getBlockSize(addon)"
                v-model.number="addonQuantities[addon.id]"
                @input="handleQuantityInput($event, addon)"
                :disabled="loadingAddons[addon.id]"
                class="addon-quantity-input"
              />
              <span class="addon-quantity-calc">
                x {{ formatCurrencyCents(addon.price, addon.currency) }}
                = {{ formatCurrencyCents(addonQuantities[addon.id] * addon.price, addon.currency) }}
                <span v-if="addon.interval">/ {{ addon.interval }}</span>
              </span>
            </div>

            <themed-button
              @click="updateAddonQuantity(addon.id, getAddonQuantity(addon.id))"
              :disabled="loadingAddons[addon.id]"
              :variant="addon.has_subscription ? 'default' : 'default'"
              class="w-full mt-3"
            >
              <span v-if="loadingAddons[addon.id]">Updating...</span>
              <span v-else-if="addon.has_subscription">Add More</span>
              <span v-else>Enable Add-on</span>
            </themed-button>
          </div>
        </div>
      </themed-card>
    </div>

    <!-- Preview Modal -->
    <subscription-preview
      v-if="showPreview && previewData"
      v-model="showPreview"
      :preview-data="previewData"
      :is-committing="isUpdating"
      :current-plan="currentPlan"
      :new-plan="selectedPlan"
      @confirm="handleCommit"
      @close="showPreview = false"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { formatCurrencyCents } from '../../../../utils/formatters';
import ThemedCard from '../ThemedCard.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedBadge from '../ThemedBadge.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemeIcon from '../ThemeIcon.vue';
import SubscriptionPreview from './SubscriptionPreview.vue';

export default {
  name: 'SubscriptionManagement',
  components: {
    ThemedCard,
    ThemedButton,
    ThemedBadge,
    ThemedInput,
    ThemeIcon,
    SubscriptionPreview
  },
  data() {
    return {
      selectedPlan: null,
      showPreview: false,
      error: null,
      loadingAddons: {},
      isUpdating: false,
      addonQuantities: {},
      currentPlan: null,
      previewData: null,
      cancellingSubscription: null,
      showCancelSubscriptionButton: false,
    };
  },
  computed: {
    ...mapState('billing', ['catalog', 'selection']),
    loading: {
      get() {
        return this.$store.state.billing.loading;
      },
      set(value) {
        this.$store.commit('billing/SET_LOADING', value);
      }
    },
    plans() {
      if (!this.catalog || !Array.isArray(this.catalog)) return [];
      return this.catalog.filter(plan => 
        plan.active !== false && 
        plan.price && 
        plan.interval &&
        plan.product_metadata?.category === 'plan'
      );
    },
    addons() {
      if (!this.catalog || !Array.isArray(this.catalog)) return [];
      return this.catalog.filter(addon => 
        addon.active !== false && 
        addon.price && 
        addon.interval &&
        addon.product_metadata?.category === 'addon'
      );
    }
  },
  async created() {
    try {
      this.loading = true;
      await this.fetchCatalog();
      
      await this.$nextTick();
      this.addons.forEach(addon => {
        const currentQty = addon.block_size;
        this.$set(this.addonQuantities, addon.id, currentQty);
      });
    } catch (error) {
      console.error('Error loading subscription data:', error);
      this.error = 'Failed to load subscription information. Please try again later.';
      this.$emit('error', this.error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions('billing', [
      'fetchCatalog',
      'setSelection',
      'preview',
      'commitChanges'
    ]),
    formatCurrencyCents(amount, currency = 'USD') {
      return formatCurrencyCents(amount || 0, currency);
    },
    formatDate(timestamp) {
      return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    hasSubscription(catalog) {
      if (catalog?.has_subscription === true) return true;
      if (this.previewData?.current_subscription?.price_id === catalog?.id) return true;
      return false;
    },
    getFeatureKey(feature) {
      if (typeof feature === 'string') return feature;
      return feature?.id || feature?.name || JSON.stringify(feature) || Math.random().toString(36).substr(2, 9);
    },
    getFeatureName(feature) {
      return typeof feature === 'string' ? feature : (feature?.name || feature?.id || '');
    },
    getBlockSize(addon) {
      return addon.block_size ? parseInt(addon.block_size) : 1;
    },
    handleQuantityInput(event, addon) {
      const newQty = parseInt(event.target.value) || 0;
      const blockSize = this.getBlockSize(addon);
      const roundedQty = Math.max(0, Math.round(newQty / blockSize) * blockSize);
      this.$set(this.addonQuantities, addon.id, roundedQty);
    },
    getAddonQuantity(priceId) {
      const addon = this.addons.find(a => a.id === priceId);
      if (!addon) return 0;
      return this.addonQuantities[priceId] !== undefined 
        ? this.addonQuantities[priceId] 
        : (addon.quantity || 0);
    },
    async selectPlan(catalog) {
      this.error = null;
      this.selectedPlan = catalog;
      await this.setSelection({ 
        priceId: catalog.id,
        subscriptionId: this.mainplan()?.stripe_subscription_id || null,
        plan: catalog,
        category: catalog.category,
        quantity: catalog.quantity,
        update: catalog.category === 'plan' ? true : this.hasSubscription(catalog)
      });
      try {
        await this.confirmSelection();
      } catch (previewError) {
        console.error('Error updating preview:', previewError);
        throw previewError;
      }
    },
    async updateAddonQuantity(priceId) {
      try {
        this.error = null;
        this.$set(this.loadingAddons, priceId, true);
        const addon = this.addons.find(a => a.id === priceId);
        if (!addon) throw new Error('Addon not found');
        const newQty = this.addonQuantities[addon.id] || 0;
        if (newQty <= 0) throw new Error('Quantity must be greater than 0');
        await this.setSelection({
          priceId: addon.id,
          subscriptionId: addon.stripe_subscription_id || null,
          plan: addon,
          category: addon.category || 'addon',
          quantity: parseInt(addon.quantity) + parseInt(newQty),
          update: addon.has_subscription
        });
        if (addon.has_subscription) {
          await this.confirmSelection();
        } else {
          await this.handleCommit();
        }
      } catch (error) {
        console.error('Error updating addon quantity:', error);
        this.error = error.message || 'Failed to update addon quantity. Please try again.';
        this.$emit('error', this.error);
      } finally {
        this.$set(this.loadingAddons, priceId, false);
      }
    },
    async confirmSelection() {
      if (!this.selection?.priceId) return false;
      try {
        this.loading = true;
        const previewData = await this.preview();
        if (previewData) {
          previewData.selection = this.selection;
          this.$set(this, 'previewData', previewData);
          this.showPreview = true;
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error fetching preview:', error);
        this.error = error.error || 'Failed to calculate changes. Please try again.';
        this.$emit('error', this.error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async handleCommit() {
      if (this.isUpdating) return;
      if (!this.selection?.priceId) {
        this.error = 'Please select a plan before continuing.';
        this.$emit('error', this.error);
        return;
      }
      try {
        this.isUpdating = true;
        this.error = null;
        this.showPreview = false;
        const result = await this.commitChanges();
        if (result?.redirecting) return;
        this.$emit('success', 'Subscription updated successfully');
        this.selectedPlan = null;
        this.previewData = null;
      } catch (error) {
        console.error('Error committing changes:', error);
        this.error = error.message || 'Failed to update subscription. Please try again.';
        this.$emit('error', this.error);
        setTimeout(() => {
          this.isUpdating = false;
        }, 1000);
      } finally {
        if (!this.error) {
          this.isUpdating = false;
        }
      }
    },
    mainplan() {
      const subscribedPlan = this.catalog.find(c => c.category === 'plan' && c.has_subscription === true);
      if (subscribedPlan) return subscribedPlan;
      if (this.previewData?.current_subscription?.price_id) {
        return this.catalog.find(c => c.id === this.previewData.current_subscription.price_id);
      }
      return null;
    },
    async confirmCancelSubscription(plan, event) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      const endDate = plan.period_end ? new Date(plan.period_end) : new Date();
      this.$swal({
        title: 'Cancel Subscription?',
        html: `
          <div class="text-left">
            <p class="mb-4">Are you sure you want to cancel your subscription?</p>
            <div class="bg-gray-50 p-4 rounded-lg mb-4">
              <p class="font-medium">Current subscription</p>
              <p class="text-lg">${plan.product?.name || 'REoptimizer'}</p>
              <p class="text-gray-600">${this.formatCurrencyCents(plan.price, plan.currency)} per ${plan.interval}</p>
            </div>
            <p class="text-sm text-gray-600 mb-2">
              Your subscription will be canceled, but is still available until the end of your billing period on
              <strong>${endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>.
            </p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#94a3b8',
        confirmButtonText: 'Yes, cancel subscription',
        cancelButtonText: 'Keep my subscription',
        reverseButtons: true
      }).then((result) => {
        if (result.value) {
          this.cancelSubscription(plan);
        }
      });
    },
    async cancelSubscription(plan) {
      this.cancellingSubscription = plan.id;
      try {
        await this.$store.dispatch('billing/cancelSubscription', plan.stripe_subscription_id);
        this.$toast?.success?.('Subscription canceled successfully');
        await this.fetchCatalog();
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        this.$toast?.error?.(error.response?.data?.message || 'Failed to cancel subscription. Please try again.');
      } finally {
        this.cancellingSubscription = null;
      }
    },
  },
};
</script>
